import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useMemo, useEffect } from 'react';
import { Table, Input, Button, Space, Tabs, Card, Tag, Modal, DatePicker, Form, message } from 'antd';
import { SearchOutlined, DeleteOutlined, FilterOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { useFetchAllBonliv, useDeleteBonliv } from '../../hooks/bonlivHooks';
import bonlivLockService from '../../services/bonlivLockService';
import { from } from 'rxjs';
import { useSelector } from 'react-redux';
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// Constants for localStorage
const ACTIVE_TAB_KEY = 'bonliv_active_tab';

// CSS styles for disabled rows
const styles = {
  disabledRow: {
    pointerEvents: 'none', // Completely disable pointer events on the row
    userSelect: 'none',    // Prevent text selection
    opacity: '0.7',        // Keep the opacity
    filter: 'grayscale(40%)',  // Add grayscale effect
    position: 'relative'   // For the overlay
  }
};



const BonlivList = ({ onSelectBonliv, userPermissions = {  isManager: false } }) => {
  // Log userPermissions when component mounts
  useEffect(() => {
    console.log('BonlivList received userPermissions:', userPermissions);
    console.log('isManager value:', userPermissions?.isManager);
    console.log('isManager type:', typeof userPermissions?.isManager);
  }, [userPermissions]);

  // Function to check if a bonliv is a complementary order
  const isComplementaryOrder = (remarque) => {
    if (!remarque) return false;
    // Check for patterns like @COMP123, @COM123, @CMP123, etc.
    return /@(COMP|COM|CMP)\s*\d{3,4}/i.test(remarque);
  };
  
  // State management
  const [clientSearch, setClientSearch] = useState('');
  const [compteSearch, setCompteSearch] = useState('');
  const [refDevSearch, setRefDevSearch] = useState('');
  const [refBlSearch, setRefBlSearch] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem(ACTIVE_TAB_KEY);
    return savedTab || 'EN_PREPARATION';
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [sorter, setSorter] = useState(() => {
    const savedTab = localStorage.getItem(ACTIVE_TAB_KEY) || 'EN_PREPARATION';
    return {
      field: 'REF_BL',
      order: savedTab === 'TRANSFERE' ? 'descend' : 'ascend'
    };
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [bonlivToDelete, setBonlivToDelete] = useState(null);
  const [lockedBonlivs, setLockedBonlivs] = useState({});
  const [currentUserLock, setCurrentUserLock] = useState(null);

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    
    // Update sorter when tab changes
    setSorter({
      field: 'REF_BL',
      order: activeTab === 'TRANSFERE' ? 'descend' : 'ascend'
    });
  }, [activeTab]);
  
  // Subscribe to lock changes - simplified approach
  useEffect(() => {
    console.log('BonlivList component mounted');
    
    // Temporarily disabled locking functionality
    /*
    // Only set user when userPermissions changes or on first mount
    bonlivLockService.setCurrentUser({
      id: 'user123', // Replace with actual user ID
      name: 'User Name', // Replace with actual user name
      isManager: userPermissions?.isManager
    });
    */
    
  }, [userPermissions]); // This effect only runs when userPermissions changes
  
  // Separate effect for subscriptions that should only run once
  useEffect(() => {
    console.log('Setting up lock subscriptions');
    
    // Temporarily disabled locking functionality
    /*
    // Initial fetch of locks - do this only once
    bonlivLockService.fetchLocks();
    
    // Just subscribe to the observables without manually syncing
    const locksSubscription = bonlivLockService.getLockedBonlivs()
      .subscribe(locks => {
        console.log('Lock state updated:', locks);
        setLockedBonlivs(locks);
      });
      
    const userLockSubscription = bonlivLockService.getCurrentUserLock()
      .subscribe(lockId => {
        console.log('Current user lock updated:', lockId);
        setCurrentUserLock(lockId);
      });
    
    // Return cleanup function
    return () => {
      console.log('BonlivList component unmounting - cleaning up subscriptions');
      locksSubscription.unsubscribe();
      userLockSubscription.unsubscribe();
    };
    */
  }, []); // Empty dependency array means this only runs once on mount

  // Combine query parameters for the hook
  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    clientSearch,
    compteSearch,
    refDevSearch,
    refBlSearch,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    status: activeTab,
  }), [pagination.current, pagination.pageSize, clientSearch, compteSearch, refDevSearch, refBlSearch, dateRange, sorter.field, sorter.order, activeTab]);

  const { bonliv, total, loading, error, refetch } = useFetchAllBonliv(params);
  const { handleDelete, loading: deleteLoading, error: deleteError } = useDeleteBonliv();
  const [hoveredRow, setHoveredRow] = useState(null);
  
  // Handle selecting a bonliv with lock check
  const handleSelectBonliv = (id) => {
    // Temporarily disabled locking functionality
    /*
    // Check if bonliv is locked - use the lockedBonlivs state directly
    if (lockedBonlivs[id]) {
      const lockInfo = lockedBonlivs[id];
      message.error(`Ce document est actuellement utilisé par ${lockInfo.userName}`);
      return;
    }
    
    // If it's not locked by anyone, try to acquire the lock
    bonlivLockService.acquireLock(id).subscribe({
      next: (response) => {
        if (response.success) {
          // We got the lock, now open the bonliv
          onSelectBonliv(id);
        }
      },
      error: (err) => {
        console.error('Lock acquisition error:', err);
        message.error(err.message || 'Failed to lock the document');
      }
    });
    */
    
    // Temporarily bypass lock check
    onSelectBonliv(id);
  };
  
  // Delete handlers
  const handleDeleteClick = (e, record) => {
    e.stopPropagation();
    setBonlivToDelete(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await handleDelete(bonlivToDelete.id);
      setDeleteModalVisible(false);
      refetch();
    } catch (err) {
      console.error('Error deleting bonliv:', err);
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearAllFilters = () => {
    setClientSearch('');
    setCompteSearch('');
    setRefDevSearch('');
    setRefBlSearch('');
    setDateRange([null, null]);
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Column definitions
  const getDateColumnTitle = () => {
    switch (activeTab) {
      case 'EN_PREPARATION': return 'Date Devis';
      case 'PREPARE': return 'Date Controle';
      case 'VALIDE': return 'Date Validation';
      case 'TRANSFERE': return 'Date Transfert';
      case 'GROUPE': return 'Date Transfert';
      case 'GRATUIT': return 'Date Transfert';
      case 'ZERO': return 'Date Transfert';
      default: return 'Date';
    }
  };

  // Admin function to force unlock a bonliv
  const handleForceUnlock = (e, id) => {
    // Temporarily disabled unlocking functionality
    /*
    e.stopPropagation();
    Modal.confirm({
      title: 'Forcer le déverrouillage',
      content: 'Êtes-vous sûr de vouloir forcer le déverrouillage de ce document? Cette action libérera le document pour tous les utilisateurs.',
      okText: 'Oui, déverrouiller',
      cancelText: 'Annuler',
      onOk: () => {
        bonlivLockService.forceUnlock(id).subscribe({
          next: (response) => {
            if (response.success) {
              message.success('Document déverrouillé avec succès');
            } else {
              message.error('Échec du déverrouillage: ' + response.message);
            }
          },
          error: (err) => {
            console.error('Force unlock error:', err);
            message.error('Échec du déverrouillage: ' + (err.message || 'Erreur inconnue'));
          }
        });
      }
    });
    */
  };

  const getLockStatusMessage = (record) => {
    // Temporarily disabled lock status message
    /*
    if (!lockedBonlivs[record.id]) {
      return null;
    }
    
    const lockInfo = lockedBonlivs[record.id];
    
    if (lockInfo) {
      return "Verrouillé";
    } else {
      return `Verrouillé`;
    }
    */
    return null;
  };

  // Add this helper function after getLockStatusMessage
  const canAdminUnlock = () => {
    console.log('MANAGER: ', userPermissions?.isManager);
    // Convert to boolean to handle string values like "true" and other truthy values
    return Boolean(userPermissions?.isManager);
  };
  
  const columns = [
    // ... other columns remain the same
    {
      title: 'Devis',
      dataIndex: 'REF_DEV',
      key: 'REF_DEV',
      sorter: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Commence par"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setRefDevSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setRefDevSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setRefDevSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Ref.',
      dataIndex: 'REF_BL',
      key: 'REF_BL',
      sorter: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Commence par"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setRefBlSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setRefBlSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setRefBlSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span 
            style={{ 
              position: 'relative',
              fontWeight: hoveredRow === record.REF_BL ? 'bold' : 'normal',
              transition: 'font-weight 0.3s ease'
            }}
          >
            {text}
            {lockedBonlivs[record.id] && (
              <span>
                <LockOutlined style={{ color: '#ff4d4f', marginLeft: 5 }} title={getLockStatusMessage(record)} />
               
              </span>
            )}
            <span
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: hoveredRow === record.REF_BL ? '100%' : '0%',
                height: '1px',
                backgroundColor: '#000',
                transition: 'width 0.3s ease'
              }}
            />
          </span>
          {/* Complementary order badge */}
          {isComplementaryOrder(record.REMARQUE) && (
            <Tag color="orange" style={{ margin: 0, fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>
              COMP
            </Tag>
          )}
        </div>
      )
    },
    {
      title: getDateColumnTitle(),
      key: 'statusDate',
      dataIndex: (record) => {
        switch (activeTab) {
          case 'EN_PREPARATION': return record.date_bl;
          case 'PREPARE': return record.date_preparation;
          case 'VALIDE': return record.date_validation;
          case 'TRANSFERE': return record.date_transfert;
          case 'GROUPE': return record.date_transfert;
          default: return record.statusDate;
        }
      },
      sorter: true,
      render: (text, record) => {
        let dateValue;
        
        switch (activeTab) {
          case 'EN_PREPARATION':
            dateValue = record.DATE_BL;
            break;
          case 'PREPARE':
            dateValue = record.date_controle;
            break;
          case 'VALIDE':
            dateValue = record.date_validation;
            break;
          case 'TRANSFERE':
          case 'GROUPE':
          case 'GRATUIT':
          case 'ZERO':
            dateValue = record.date_transfert;
            break;
          default:
            dateValue = record.statusDate;
        }
        
        return dateValue ? new Date(dateValue).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-';
      },
    },
    {
      title: 'Compte',
      dataIndex: 'COMPTE',
      key: 'COMPTE',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher Compte"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setCompteSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setCompteSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setCompteSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Client',
      dataIndex: 'CLIENT',
      key: 'CLIENT',
      width: 400,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher Client"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setClientSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setClientSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setClientSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Total TTC',
      dataIndex: 'MNT_TTC',
      key: 'MNT_TTC',
      sorter: true,
      render: value => value?.toFixed(2),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          EN_PREPARATION: { color: 'purple', text: 'En Preparation' },
          PREPARE: { color: 'cyan', text: 'Controle' },
          VALIDE: { color: 'green', text: 'Validé' },
          TRANSFERE: { color: 'blue', text: 'Transferé' },
          GROUPE: { color: 'orange', text: 'Transféré Groupe' },
          GRATUIT: { color: 'gold', text: 'Gratuit' },
          ZERO: { color: 'red', text: 'Zéro' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'EN_PREPARATION' && (
            <PermissionButton permissionModule="deliveryNotes" permissionAction="delete"
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => handleDeleteClick(e, record)}
              disabled={Boolean(lockedBonlivs[record.id])}
            />
          )}
          
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
  };

  const getRowStyle = (record) => {
    let style = {};
    
    // Base styling for status
    switch (record.status) {
      case 'TRANSFERE':
        style = { backgroundColor: '#e6f7ff' }; // Light blue for transferred
        break;
      case 'VALIDE':
        style = { backgroundColor: '#f6ffed' }; // Light green for validated
        break;
      case 'GROUPE':
        style = { backgroundColor: '#fff7e6' }; // Light orange for groupe
        break;
      case 'GRATUIT':
        style = { backgroundColor: '#fffbe6' }; // Light gold for gratuit
        break;
      case 'ZERO':
        style = { backgroundColor: '#fff1f0' }; // Light red for zero
        break;
      default:
        style = {};
    }
    
    // Temporarily disabled lock styling
    /*
    // Check if the record is locked using the lockedBonlivs state directly
    if (lockedBonlivs[record.id]) {
      style = { 
        ...style, 
        backgroundColor: '#f5f5f5', // Grey for all locks
        color: '#999999',
        borderLeft: '4px solid #ff4d4f' // Red for all locks
      };
    }
    */
    
    return style;
  };

  return (
    <div>
      <Card style={{ marginTop: 20, backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>
          Listes Des Bon de Livraison
        </h2>

        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="dateRange" label="Période">
            <RangePicker 
              onChange={handleDateChange}
              value={dateRange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
            />
          </Form.Item>
          <Form.Item>
            <Button 
              icon={<FilterOutlined />} 
              onClick={clearAllFilters}
            >
              Réinitialiser filtres
            </Button>
          </Form.Item>
        </Form>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          {/* Left tabs - Workflow statuses */}
          <Tabs 
            activeKey={['EN_PREPARATION', 'PREPARE', 'VALIDE'].includes(activeTab) ? activeTab : null} 
            onChange={setActiveTab}
            size="small"
          >
            <TabPane tab="Préparation" key="EN_PREPARATION" />
            <TabPane tab="Contrôle" key="PREPARE" />
            <TabPane tab="Validé" key="VALIDE" />
          </Tabs>

          {/* Right tabs - Final statuses */}
          <Tabs 
            activeKey={['TRANSFERE', 'GROUPE', 'GRATUIT', 'ZERO'].includes(activeTab) ? activeTab : null} 
            onChange={setActiveTab}
            size="small"
          >
            <TabPane tab="Transféré" key="TRANSFERE" />
            <TabPane tab="Groupe" key="GROUPE" />
            <TabPane tab="Gratuit" key="GRATUIT" />
            <TabPane tab="Zéro" key="ZERO" />
          </Tabs>
        </div>

        <Table
          columns={columns}
          dataSource={bonliv}
          rowKey="REF_BL"
          loading={loading}
          pagination={{ ...pagination, total }}
          onChange={handleTableChange}
          onRow={(record) => {
            // Temporarily disabled lock checks
            /*
            // Correctly check if the record is locked by directly checking lockedBonlivs state
            const isLocked = Boolean(lockedBonlivs[record.id]);
            
            // Allow admin users to click locked rows for the purpose of unlocking
            const isManager = canAdminUnlock();
            
            return {
              onClick: isLocked ? 
                     isManager ? () => {
                       // For admins, show a modal to choose between viewing the warning or unlocking
                       Modal.confirm({
                         title: 'Document verrouillé',
                         content: `Ce document est actuellement utilisé par ${lockedBonlivs[record.id]?.userName || 'un utilisateur'}. Voulez-vous le déverrouiller?`,
                         okText: 'Déverrouiller',
                         cancelText: 'Annuler',
                         onOk: () => handleForceUnlock(new Event('click'), record.id)
                       });
                     } : undefined : 
                     () => handleSelectBonliv(record.id),
              onMouseEnter: () => setHoveredRow(record.REF_BL),
              onMouseLeave: () => setHoveredRow(null),
              style: { 
                cursor: isLocked ? (isManager ? 'pointer' : 'not-allowed') : 'pointer',
                ...(isLocked && !isManager ? styles.disabledRow : {}),
                ...getRowStyle(record)
              }
            };
            */
            
            // Temporarily simplified row handling without locks
            return {
              onClick: () => handleSelectBonliv(record.id),
              onMouseEnter: () => setHoveredRow(record.REF_BL),
              onMouseLeave: () => setHoveredRow(null),
              style: getRowStyle(record)
            };
          }}
          size="small"
        />

        <Modal
          title="Confirmer la suppression"
          visible={deleteModalVisible}
          onOk={handleDeleteConfirm}
          onCancel={() => setDeleteModalVisible(false)}
          confirmLoading={deleteLoading}
          okText="Oui"
          cancelText="Non"
        >
          <p>Êtes-vous sûr de vouloir supprimer ce BL?</p>
          {deleteError && <p style={{ color: 'red' }}>{deleteError}</p>}
        </Modal>
      </Card>
    </div>
  );
};

export default BonlivList;